import { useDebouncedValue } from "@mantine/hooks";
import { GraphQL } from "@rpi/openapi-api";
import { FILTERABLE_PAYMENT_STATUSES } from "../../utils/paymentStatusNames";
import React from "react";
import { RpiFilterV2 } from "../../components/search-filter/RpiSearchFilterTypes";
import { useAuthUser } from "../../hooks/useAuthUser";
import { optArray } from "../../utils/misc.util";
import { EqualsSearchInput } from "./searchInput";

/** Scopes an orders query to one environment. Applied automatically, never offered as a user-selectable filter. */
export const sandboxOrderFilter = (isSandbox: boolean): EqualsSearchInput<GraphQL.OrderSearchField> => ({
  condition: GraphQL.SearchCondition.Equals,
  field: GraphQL.OrderSearchField.IsSandbox,
  firstValue: isSandbox.toString(),
});

export const useOrderFilters = () => {
  const user = useAuthUser();

  const [customersSearch, setCustomersSearch] = React.useState("");
  const [debouncedCustomersSearch] = useDebouncedValue(customersSearch, 250);

  const { data, isLoading } = GraphQL.useAdminListCustomersQuery(
    { input: debouncedCustomersSearch },
    { enabled: user.is.SUPER_ADMIN }
  );
  const customers = React.useMemo(() => (data?.adminListCustomers || []).map((customer) => customer.publicId), [data]);

  return React.useMemo(() => {
    return RpiFilterV2.createConfigs<GraphQL.OrderSearchField>([
      ...optArray<RpiFilterV2.Config<GraphQL.OrderSearchField>>(user.is.SUPER_ADMIN, [
        {
          type: "dropdown",
          label: "Customer",
          field: GraphQL.OrderSearchField.CustomerPublicId,
          data: customers,
          onSearch: setCustomersSearch,
          isLoading: isLoading,
        },
      ]),
      {
        type: "select",
        label: "Status",
        field: GraphQL.OrderSearchField.Status,
        data: Object.values(GraphQL.PublicOrderStatus),
      },
      {
        type: "select",
        label: "Payment Status",
        field: GraphQL.OrderSearchField.PaymentStatus,
        data: [...FILTERABLE_PAYMENT_STATUSES],
      },
      {
        type: "date",
        label: "Created at",
        field: GraphQL.OrderSearchField.CreatedAt,
      },
      {
        type: "date",
        label: "Updated at",
        field: GraphQL.OrderSearchField.UpdatedAt,
      },
      {
        type: "date",
        label: "Submitted At",
        field: GraphQL.OrderSearchField.SubmittedAt,
      },
    ]);
  }, [user.is, customers, isLoading]);
};
